import { type RefObject, useEffect, useRef } from 'react'

const AUTO_FOCUS_SELECTOR =
  '[data-autofocus]:not(:disabled):not([aria-disabled="true"])'

/**
 * Focuses the first eligible `[data-autofocus]` field in a form,
 * including fields mounted asynchronously (e.g. via Suspense).
 */
export const useFocusFirstAutofocusField = (
  formRef: RefObject<HTMLFormElement | null>,
) => {
  const hasFocusedRef = useRef(false)

  useEffect(() => {
    if (hasFocusedRef.current) {
      return
    }

    const form = formRef.current
    if (!form) {
      return
    }

    const tryFocusFirstInput = () => {
      // We could check if form.closest('[role="dialog"]') if we wanted to only focus fields in modals, but at least for now we want to focus autofocusable fields in any form.
      const focusTarget = form.querySelector<HTMLElement>(AUTO_FOCUS_SELECTOR)

      if (!focusTarget) {
        return false
      }

      const hasUserFocusedInsideForm =
        document.activeElement instanceof HTMLElement &&
        form.contains(document.activeElement) &&
        document.activeElement !== form

      if (!hasUserFocusedInsideForm) {
        focusTarget.focus()
        if (document.activeElement !== focusTarget) {
          return false
        }
      }
      hasFocusedRef.current = true
      return true
    }

    if (tryFocusFirstInput()) {
      return
    }

    const observer = new MutationObserver(() => {
      if (tryFocusFirstInput()) {
        observer.disconnect()
      }
    })

    observer.observe(form, {
      // Async forms can mount the input several levels deep, so we must observe
      // both direct child changes and all descendants to catch autofocus fields.
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [formRef])
}
